import { Picker } from '@/components/Picker'
/**
 * Engineer documentation page.
 * Current developer guide and repository references, followed by read-only data access
 * instructions using the resource names returned by /eng/storage.
 */
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DOCUMENTS as DOCS } from './documents'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { Section } from '@/components/Section'
import { Markdown } from '@/components/Markdown'
import { REVIEW_STAGES } from '@/lib/stages'

interface StorageInfo { region: string; table: string; uploads_bucket: string; verifier_bucket: string; programs: string[]; key_layout: string[] }

const DOCUMENT_LINKS = Object.fromEntries(DOCS.map(doc => [doc.name, `/eng/docs?doc=${doc.name}`]))

export default function EngDocs() {
  const { data: info, error } = useAsync(() => api.get<StorageInfo>('/eng/storage'))
  const [params, setParams] = useSearchParams()
  const doc = DOCS.find(d => d.name === params.get('doc')) ?? DOCS[0]
  const docName = doc.name
  const setDocName = (name: string) => setParams({ doc: name })

  return (
    <div>
      <div className="page-head"><div><h1>Docs</h1><div className="meta">Workflow guide, technical references and task data access.</div></div></div>
      {error && <div className="note err">{error}</div>}

      <Section title="Project documentation" >
        <div className="doc-layout"><nav className="doc-nav" aria-label="Documents">{DOCS.map(d => <button key={d.name} className={d.name === docName ? 'active' : ''} onClick={() => setDocName(d.name)}>{d.title}</button>)}</nav><div>
        <label className="field doc-select" style={{ maxWidth: 480 }}>
          <span>Document</span>
          <Picker label="Document" field value={docName} onChange={setDocName}
            options={DOCS.map(d => ({ value: d.name, label: d.title, detail: d.name }))} />
        </label>
        {doc && <div className="doc-body"><Markdown source={doc.body} documentLinks={DOCUMENT_LINKS} /></div>}</div></div>
      </Section>

      <Section title="Read task data" lead="Download current task records and uploads. Resource names come from this environment; frozen review evidence and published packages have separate references.">
        {info ? <AccessInstructions info={info} /> : <p className="muted">Loading…</p>}
      </Section>
    </div>
  )
}

// ------------------------------------------------------------------ access instructions

type Mode = 'cli' | 'prompt'
type Scope = 'task' | 'program'

function AccessInstructions({ info }: { info: StorageInfo }) {
  const [mode, setMode] = useState<Mode>('cli')
  const [scope, setScope] = useState<Scope>('task')
  const [taskId, setTaskId] = useState('task_xxxxxxxxxxxx')
  const [program, setProgram] = useState(info.programs[0] ?? 'Fusion')
  const [copied, setCopied] = useState(false)

  const text = useMemo(() => (mode === 'cli' ? cliText(info, scope, taskId, program) : promptText(info, scope, taskId, program)), [info, mode, scope, taskId, program])

  async function copy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* clipboard blocked; user can select the text */ }
  }

  return (
    <div>
      <div className="access-controls">
        <div className="seg" role="tablist" aria-label="Format">
          <button type="button" className={mode === 'cli' ? 'on' : ''} onClick={() => setMode('cli')}>CLI</button>
          <button type="button" className={mode === 'prompt' ? 'on' : ''} onClick={() => setMode('prompt')}>Prompt</button>
        </div>
        <div className="seg" role="tablist" aria-label="Scope">
          <button type="button" className={scope === 'task' ? 'on' : ''} onClick={() => setScope('task')}>A single task</button>
          <button type="button" className={scope === 'program' ? 'on' : ''} onClick={() => setScope('program')}>All tasks of a program</button>
        </div>
        {scope === 'task'
          ? <input type="text" value={taskId} onChange={(e) => setTaskId(e.target.value.trim())} placeholder="task_…" aria-label="Task id" style={{ maxWidth: 240, fontFamily: 'ui-monospace, Menlo, monospace' }} />
          : <Picker label="Program" value={program} onChange={setProgram} options={info.programs.map(value => ({ value, label: value }))} />}
        <button type="button" className="sm" onClick={copy} style={{ marginLeft: 'auto' }}>{copied ? 'Copied' : mode === 'cli' ? 'Copy commands' : 'Copy prompt'}</button>
      </div>
      <pre className="access-text">{text}</pre>
      <dl className="kv access-facts">
        <div><dt>Region</dt><dd>{info.region}</dd></div>
        <div><dt>DynamoDB table</dt><dd>{info.table}</dd></div>
        <div><dt>Uploads bucket</dt><dd>{info.uploads_bucket}</dd></div>
        {info.verifier_bucket && <div><dt>Verifier bucket</dt><dd>{info.verifier_bucket}</dd></div>}
        <div><dt>Object key layout</dt><dd>{info.key_layout.join('\n')}</dd></div>
        <div><dt>Stages</dt><dd>{STAGE_NOTE}</dd></div>
      </dl>
    </div>
  )
}

const STAGE_NOTE = `All tasks use V2: ${REVIEW_STAGES.map(s => `${s.stage} ${s.label}`).join(' → ')}. Stored stage IDs are unchanged. Confirm publication.status is published before using publication.commit as the published package.`

// Generated commands download data only; quote values before including them in shell code.
function shellQuote(value: string): string { return "'" + value.replaceAll("'", "'\\''") + "'" }

function cliText(info: StorageInfo, scope: Scope, taskId: string, program: string): string {
  const head = `# Prerequisites: AWS CLI v2 with credentials for the account (e.g. an SSO profile), jq.
export AWS_PROFILE='your-profile' # Replace with your configured read-only profile.
export AWS_REGION=${shellQuote(info.region)}
TABLE=${shellQuote(info.table)}
BUCKET=${shellQuote(info.uploads_bucket)}
`
  const perTask = (idVar: string) => `# The task record: brief, prompt, rubric, stage/workflow_version, publication, review_round_id, verifier build, QC, the expert's
# submission notes, and parent_task_id when the task continues another task.
aws dynamodb get-item --table-name "$TABLE" \\
  --key "$(jq -nc --arg id "${idVar}" '{pk:{S:("TASK#"+$id)},sk:{S:"META"}}')" --output json > "${idVar}/task.json"

# The file records: kind (video, final_output, state, initial_state, example_positive, example_negative, attachment),
# criterion_id for examples, and the S3 key of each object.
aws dynamodb query --table-name "$TABLE" \\
  --key-condition-expression "pk = :p AND begins_with(sk, :f)" \\
  --expression-attribute-values "$(jq -nc --arg id "${idVar}" '{":p":{S:("TASK#"+$id)},":f":{S:"FILE#"}}')" --output json > "${idVar}/files.json"

# Every uploaded object, in the readable key layout (submission/video = the recording,
# submission/state = the backend-state tracking export, submission/initial_state = the tracker export from
# before the expert started, examples/<criterion>/positive|negative, attachments).
aws s3 sync "s3://$BUCKET/tasks/${idVar}/" "${idVar}/files/"`

  if (scope === 'task') {
    return `${head}TASK=${shellQuote(taskId)}
mkdir -p "$TASK"

${perTask('$TASK')}

# ${STAGE_NOTE}
jq -r '.Item | "stage \\(.stage.N)  \\(.title.S)"' "$TASK/task.json"
# Note: DynamoDB JSON is typed ({"S": ...}, {"N": ...}); pipe through jq or load it with boto3's
# resource API (dynamodb.Table(...).get_item) to get plain values.`
  }
  return `${head}PROGRAM=${shellQuote(program)}

# 1. List current metadata for the program. AWS CLI paginates the query.
aws dynamodb query --table-name "$TABLE" --index-name gsi3 \\
  --key-condition-expression "gsi3pk = :p" --filter-expression "contains(programs, :prog) OR program = :prog" \\
  --expression-attribute-values "$(jq -nc --arg program "$PROGRAM" '{":p":{S:"ENTITY#TASK"},":prog":{S:$program}}')" \\
  --output json > tasks.json

# 2. Export verified publications.
jq -r '.Items[] | select(.stage.N == "5") | select(.publication.M.status.S == "published") | .task_id.S' tasks.json |
while IFS= read -r TASK; do
  mkdir -p "$TASK"
${perTask('$TASK').split('\n').map((l) => (l ? '  ' + l : l)).join('\n')}
done

# ${STAGE_NOTE}
# Organisation: one folder per task_id; task.json describes it, files.json indexes the objects,
# files/ mirrors the S3 keys: the recording(s) under submission/video/, the state-tracking export(s)
# under submission/state/, the initial-state export(s) under submission/initial_state/, rubric examples under examples/<criterion_id>/.`
}

function promptText(info: StorageInfo, scope: Scope, taskId: string, program: string): string {
  const common = `You have AWS credentials (profile <your-profile>) for the account that hosts the Seldon task pipeline, region ${info.region}.

Where the data lives:
- DynamoDB table "${info.table}", single-table design. A task item has pk = "TASK#<task_id>" and sk = "META". Its file records have the same pk and sk starting with "FILE#". Global secondary index "gsi3" lists all tasks under gsi3pk = "ENTITY#TASK" (each item carries task_id, title, program, stage).
- S3 bucket "${info.uploads_bucket}". Objects of a task live under tasks/<task_id>/ with this layout:
${info.key_layout.map((k) => `    ${k}`).join('\n')}

What a task item contains: title, programs (and the single-program compatibility field program), prompt (the request the AI receives), brief (13 answers about the real work), rubric (criteria with criterion_id, criterion, role, evidence and optional rationale/decision_anchor; pass_gate may remain in older records), stage, workflow_version, coach_review, review_round_id, publication (status and commit), review_notes, build (verifier/qcow2 locations, per-criterion validation), qc (rollouts, pass rate), submission (the expert's notes, alternative_approaches and empty_start: the task starts from a blank document, so no initial state is required) and parent_task_id when the task continues the outcome of another task. The expert's execution has four file slots, each one or more files of any type (at most 10 per part): the starting materials needed before execution (kind initial_state, under submission/initial_state/; waived when empty_start is set), the screen recording (kind video, under submission/video/) the completed final files (kind final_output, under submission/final_output/), and the optional backend-state tracking export (kind state, under submission/state/). File records give kind (video, final_output, state, initial_state, example_positive, example_negative, attachment), criterion_id for examples, and s3_key.

${STAGE_NOTE}

Review rounds, recommendations, draft-review snapshots and QC jobs are separate task children; draft and peer coach chats use separate conversation partitions. This export covers current task data, not the full review history. Use the review API for frozen evidence and the recorded package reference for published artifacts.`

  if (scope === 'task') {
    return `${common}

Task: fetch the current task record and uploads for task "${taskId}". Read the task item and its file records from DynamoDB (use boto3's resource API so values come back as plain Python types), download every object under tasks/${taskId}/ from S3 into a folder named after the task id, and write a short README.md in that folder summarising: the prompt, the rubric, the stage, and the local paths of the initial-state files, the recording(s) and the state-tracking files. Do not modify anything in AWS.`
  }
  return `${common}

Task: collect completed tasks (stage == 5; require publication.status == published) of the program "${program}". Query gsi3 with gsi3pk = "ENTITY#TASK" and keep items whose programs list contains "${program}" (or whose older single program field matches) and stage equals 5; require publication.status == published. For each, read the task item and its file records (boto3 resource API), download every object under tasks/<task_id>/ into a folder named after the task id, and write an index.md listing each task with its title, task id, workflow version, publication commit when present, and local folder. Inside each folder add a README.md with the prompt, the rubric and the local paths of the initial-state files, the recording(s) and the state-tracking files. Do not modify anything in AWS.`
}
